/**
 * The command interface shared by the human player and the AI (DESIGN §5, §9).
 * Each frame, a controller produces a Command; the Bey entity consumes it.
 * Keeping this a plain data type means the AI "plays the game" the same way a
 * human does — and stays unit-testable.
 */
import type { Vec2 } from './vec.ts';
import type { BeyType } from './stats.ts';

export interface Command {
  /** Steering direction. Magnitude 0..1; zero means no input. */
  steer: Vec2;
  /** Strike/dash this frame (edge-triggered by the controller). */
  dash: boolean;
  /** Begin an extreme dash this frame (edge-triggered). */
  extremeDash: boolean;
  /** Hold the defensive center-anchor (level-triggered while held). */
  holdCenter: boolean;
  /** Fire the bey's special this frame (edge-triggered; needs a full meter). */
  special: boolean;
}

export const NO_COMMAND: Command = {
  steer: { x: 0, y: 0 },
  dash: false,
  extremeDash: false,
  holdCenter: false,
  special: false,
};

/** A read-only snapshot of one bey, given to a controller to decide its move. */
export interface BeySnapshot {
  /** Archetype (for matchup-aware AI). */
  type: BeyType;
  pos: Vec2;
  vel: Vec2;
  spin: number;
  maxSpin: number;
  burst: number;
  burstMax: number;
  /** Seconds until dash is usable again (0 = ready). */
  dashCooldown: number;
  /** Seconds until extreme dash is usable again (0 = ready). */
  extremeCooldown: number;
  /** Special charge meter (0..specialMax). */
  special: number;
  specialMax: number;
}

/** The full world view passed to a controller each decision tick. */
export interface WorldSnapshot {
  self: BeySnapshot;
  opponent: BeySnapshot;
  arenaCenter: Vec2;
  rimRadius: number;
}

/** A controller turns a world snapshot into a command. */
export interface Controller {
  decide(world: WorldSnapshot, dt: number): Command;
}
